import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { StickyBar, StickyBarVariant, useLockBodyScroll, useScrollPosition } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { LISTER_TILES_ID, } from '@as-react/common-types';
import { SlidersHorizontalFillIcon } from '@yonderland/andes-icons';
import { Box, BottomSheet, TextAlpha, Stack, Button, Icon } from '@yonderland/andes-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HEADER_CONTAINER, NAVIGATION_HEIGHT_MOBILE, NAVIGATION_HORIZONTAL, NAVIGATION_HORIZONTAL_COLLAPSE, } from '../../../../constants/DOMElementsConstants';
import { DELAY } from '../../../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useThrottle } from '../../../../hooks/useThrottle';
import { NAVIGATION_MENU_OFFSET, ScrollUtil } from '../../../../util/ScrollUtil';
import { FiltersActiveMobile } from '../filters-active/FiltersActiveMobile';
import { InStoreFilter } from '../InStoreFilter';
import { FiltersMobileButton } from './FiltersMobileButton';
import { FiltersMobileList } from './FiltersMobileList';
var VerticalDirection;
(function (VerticalDirection) {
    VerticalDirection["UP"] = "up";
    VerticalDirection["DOWN"] = "down";
})(VerticalDirection || (VerticalDirection = {}));
const BUTTON_TEXT_WHEN_FILTERS_CLOSED = 'product:product.lister.filter.button';
export const FiltersMobile = ({ filters, fredhopperGlossary, isContentTileLister, itemCount, onFilterItemChange, removeAllFilters, urlFilters, ccPopupCollapsed, }) => {
    const { t } = useTranslation([I18N_NAMESPACES.product]);
    const { feature_toggles: { lister_in_store_filter }, } = useAemConfig();
    const [navigationEl, setNavigationEl] = useState();
    const [isStickyFiltersOpen, setIsStickyFiltersOpen] = useState(false);
    const [isButtonHidden, setIsButtonHidden] = useState(true);
    const buttonRef = useRef(null);
    const isFilterChanged = useRef(false);
    const getStickyButtonText = () => {
        if (!isStickyFiltersOpen) {
            return isContentTileLister ? 'general:content.overview.filter.title' : BUTTON_TEXT_WHEN_FILTERS_CLOSED;
        }
        return isContentTileLister
            ? 'general:content.overview.filter.articles'
            : 'product.lister.filter.button.show_result';
    };
    const displayHandler = useThrottle((direction, button, navigation, listerContainer) => {
        if (!button) {
            return;
        }
        const headerContainer = document.querySelector(HEADER_CONTAINER);
        const { top, bottom } = button.getBoundingClientRect();
        const buttonOffset = direction === VerticalDirection.UP ? top : bottom;
        const isNavDisplayed = !!navigation && !navigation.classList.contains(NAVIGATION_HORIZONTAL_COLLAPSE);
        const headerBottom = headerContainer === null || headerContainer === void 0 ? void 0 : headerContainer.getBoundingClientRect().bottom;
        const viewportTopOffset = Math.max(0, typeof headerBottom === 'number' && headerBottom > 0
            ? Math.floor(headerBottom)
            : isNavDisplayed
                ? NAVIGATION_HEIGHT_MOBILE
                : 0);
        const listerContainerBottom = listerContainer === null || listerContainer === void 0 ? void 0 : listerContainer.getBoundingClientRect().bottom;
        const isPastListerBottom = typeof listerContainerBottom === 'number' ? window.innerHeight - listerContainerBottom > 0 : false;
        setIsButtonHidden(Boolean(isPastListerBottom || Math.floor(buttonOffset - viewportTopOffset) >= 0));
    }, DELAY.DELAY_500);
    useEffect(() => {
        var _a;
        const navigation = document.querySelector(NAVIGATION_HORIZONTAL);
        const listerContainer = (_a = document.getElementById(LISTER_TILES_ID)) !== null && _a !== void 0 ? _a : undefined;
        setNavigationEl(navigation !== null && navigation !== void 0 ? navigation : undefined);
        displayHandler(VerticalDirection.DOWN, buttonRef.current, navigation !== null && navigation !== void 0 ? navigation : undefined, listerContainer);
    }, [displayHandler]);
    useScrollPosition(({ prevPos, currPos }) => {
        const direction = prevPos.y < currPos.y ? VerticalDirection.UP : VerticalDirection.DOWN;
        const listerContainer = document.getElementById(LISTER_TILES_ID);
        displayHandler(direction, buttonRef.current, navigationEl, listerContainer);
    }, [buttonRef, navigationEl]);
    useEffect(() => {
        isFilterChanged.current = true;
    }, [urlFilters]);
    const withNavBar = typeof document !== 'undefined' && (!!document.querySelector(HEADER_CONTAINER) || !!navigationEl);
    const handleCloseBottomSheet = () => {
        scrollIntoView();
        isFilterChanged.current = false;
        setIsStickyFiltersOpen(false);
    };
    const handleToggleStickyFiltersOpen = () => {
        scrollIntoView();
        isFilterChanged.current = false;
        setIsStickyFiltersOpen(!isStickyFiltersOpen);
    };
    const scrollIntoView = () => {
        const listerContainer = document.getElementById(LISTER_TILES_ID);
        listerContainer &&
            isFilterChanged.current &&
            isStickyFiltersOpen &&
            !isButtonHidden &&
            ScrollUtil.scrollToPosition(listerContainer.offsetTop - NAVIGATION_MENU_OFFSET);
    };
    useLockBodyScroll(isStickyFiltersOpen);
    return (_jsxs("div", { "data-testid": "filters-mobile", children: [_jsx("div", { ref: buttonRef, children: _jsx(FiltersMobileButton, { text: isContentTileLister
                        ? t('general:content.overview.filter.articles', {
                            count: itemCount,
                        })
                        : t(BUTTON_TEXT_WHEN_FILTERS_CLOSED), onClick: handleToggleStickyFiltersOpen, isContentTileLister: isContentTileLister }) }), ccPopupCollapsed && (_jsx(StickyBar, { isHidden: !isStickyFiltersOpen && isButtonHidden, collapsed: !isStickyFiltersOpen, variant: StickyBarVariant.FILTERS, withNavBar: withNavBar, children: _jsxs(Button, { theme: "branded", onClick: handleToggleStickyFiltersOpen, fullWidth: isStickyFiltersOpen, children: [!isStickyFiltersOpen && _jsx(Icon, { source: SlidersHorizontalFillIcon, size: "sm" }), t(getStickyButtonText(), {
                            count: itemCount,
                        })] }) })), _jsx(BottomSheet, { isOpen: isStickyFiltersOpen, fullHeightOnScroll: true, padding: "slim", onClose: handleCloseBottomSheet, children: _jsxs(Box, { paddingBlockEnd: "16", children: [_jsxs(Stack, { direction: "col", gap: "6", children: [_jsx(TextAlpha, { variant: "subheadingL", children: t(isContentTileLister ? 'general:content.overview.filter.title' : 'product:product.lister.filter.title') }), _jsxs(Stack, { direction: "col", gap: "4", children: [_jsx(FiltersActiveMobile, {}), _jsx(FiltersMobileList, { onFilterItemChange: onFilterItemChange, filters: filters, itemCount: itemCount, isContentTileLister: isContentTileLister, removeAllActiveFilters: removeAllFilters, fredhopperGlossary: fredhopperGlossary })] })] }), !isContentTileLister && lister_in_store_filter && _jsx(InStoreFilter, {})] }) })] }));
};
